import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  Mail,
  MailCheck,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { changePassword, sendPasswordOtp } from "../api/password";

type Step = "email" | "sent";
type Status = { tone: "success" | "error"; text: string } | null;

const initialEmailFromLocation = (location: ReturnType<typeof useLocation>) => {
  const stateEmail = (location.state as { email?: string } | null)?.email;
  const queryEmail = new URLSearchParams(location.search).get("email");
  return (stateEmail || queryEmail || "").trim();
};

export default function ForgotPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState(() => initialEmailFromLocation(location));
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  const normalizedEmail = email.trim();

  const sendCode = async () => {
    if (!normalizedEmail) {
      setStatus({ tone: "error", text: "Enter your work email first." });
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      await sendPasswordOtp(normalizedEmail);
      setEmail(normalizedEmail);
      setStep("sent");
      setStatus({ tone: "success", text: `Verification code sent to ${normalizedEmail}` });
    } catch (err: any) {
      setStatus({ tone: "error", text: err?.message || "Could not send verification code." });
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async () => {
    const code = otp.trim();
    if (code.length < 6) {
      setStatus({ tone: "error", text: "Enter the 6-digit verification code." });
      return;
    }
    if (newPassword.length < 6) {
      setStatus({ tone: "error", text: "Password must be at least 6 characters." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus({ tone: "error", text: "Passwords do not match." });
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      await changePassword(normalizedEmail, code, newPassword);
      navigate("/login");
    } catch (err: any) {
      setStatus({ tone: "error", text: err?.message || "Could not update password." });
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step === "email") {
      await sendCode();
    } else {
      await submitNewPassword();
    }
  };

  const buttonLabel = step === "email" ? "Send code to my email" : "Update password";
  const busyLabel = step === "email" ? "Sending code..." : "Updating password...";

  return (
    <main className="fp-shell">
      <style>{`
        .fp-shell {
          --fp-ink: #151716;
          --fp-muted: #76716b;
          --fp-paper: #f8f6f1;
          --fp-panel: #fffdf8;
          --fp-line: #ded8cb;
          --fp-primary: #0f2d1f;
          --fp-primary-soft: #e7efe9;
          --fp-gold: #d4a356;
          --fp-success: #217a52;
          --fp-error-bg: #fff2ef;
          --fp-error: #8f2d1f;
          min-height: 100dvh;
          position: relative;
          overflow: hidden;
          color: var(--fp-ink);
          background:
            radial-gradient(circle at 86% 12%, rgba(231, 239, 233, 0.9), transparent 28rem),
            var(--fp-paper);
          font-family: 'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .fp-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.08fr) minmax(420px, 0.82fr);
          min-height: 100dvh;
        }
        .fp-side {
          color: #faf8f3;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: clamp(32px, 5vw, 72px);
          position: relative;
          isolation: isolate;
          overflow: hidden;
          background: var(--fp-primary);
        }
        .fp-side::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          opacity: 0.18;
          background-image: linear-gradient(rgba(250, 248, 243, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(250, 248, 243, 0.08) 1px, transparent 1px);
          background-size: 44px 44px;
          mask-image: linear-gradient(90deg, #000, transparent 90%);
        }
        .fp-brand {
          min-height: 48px;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          align-self: flex-start;
          border: 0;
          background: transparent;
          color: inherit;
          cursor: pointer;
          padding: 0;
          text-align: left;
        }
        .fp-brand-mark {
          width: 44px;
          height: 44px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #faf8f3;
          color: var(--fp-primary);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
        }
        .fp-serif { font-family: 'Fraunces', 'Iowan Old Style', Georgia, serif !important; }
        .fp-mono { font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace !important; }
        .fp-eyebrow {
          font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-weight: 650;
        }
        .fp-side-title {
          color: #faf8f3 !important;
          font-size: clamp(42px, 5.4vw, 74px);
          line-height: 0.94;
          font-weight: 550;
          letter-spacing: 0;
          max-width: 680px;
        }
        .fp-side .fp-brand .fp-serif {
          color: #faf8f3 !important;
        }
        .fp-side-copy {
          margin-top: 24px;
          max-width: 520px;
          color: #aec4b5;
          font-size: 16px;
          line-height: 1.7;
        }
        .fp-proof-row {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          width: 100%;
          max-width: min(720px, 100%);
        }
        .fp-proof {
          border: 1px solid rgba(250, 248, 243, 0.16);
          background: rgba(250, 248, 243, 0.07);
          border-radius: 18px;
          padding: 16px;
          min-height: 112px;
        }
        .fp-proof svg { color: var(--fp-gold); }
        .fp-proof strong {
          display: block;
          color: #faf8f3;
          margin-top: 16px;
          font-size: 14px;
        }
        .fp-proof span {
          display: block;
          color: #aec4b5;
          margin-top: 4px;
          font-size: 12px;
          line-height: 1.45;
        }
        .fp-form-side {
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(24px, 4vw, 64px);
          background: var(--fp-paper);
        }
        .fp-panel {
          width: min(100%, 480px);
        }
        .fp-mobile-brand { display: none; }
        .fp-progress {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 28px;
        }
        .fp-progress-track {
          display: inline-grid;
          grid-template-columns: 32px 32px;
          gap: 8px;
          align-items: center;
        }
        .fp-progress-dot {
          height: 8px;
          border-radius: 999px;
          background: var(--fp-line);
          transition: width 220ms ease, background 220ms ease;
        }
        .fp-progress-dot[data-active="true"] {
          width: 32px;
          background: var(--fp-primary);
        }
        .fp-progress-dot[data-active="false"] {
          width: 8px;
          justify-self: center;
        }
        .fp-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 32px;
          border-radius: 999px;
          padding: 6px 10px;
          background: var(--fp-primary-soft);
          color: var(--fp-primary);
          margin-bottom: 18px;
        }
        .fp-title {
          color: var(--fp-primary);
          font-size: clamp(38px, 4.4vw, 54px);
          line-height: 0.98;
          font-weight: 600;
          letter-spacing: 0;
          margin: 0;
        }
        .fp-lede {
          color: #4a4a4a;
          font-size: 15px;
          line-height: 1.65;
          margin: 16px 0 0;
          max-width: 420px;
        }
        .fp-status {
          margin-top: 22px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          border-radius: 18px;
          padding: 12px 14px;
          font-size: 13px;
          line-height: 1.45;
        }
        .fp-status[data-tone="success"] {
          background: var(--fp-primary-soft);
          color: var(--fp-primary);
        }
        .fp-status[data-tone="error"] {
          background: var(--fp-error-bg);
          color: var(--fp-error);
        }
        .fp-form {
          margin-top: 30px;
          display: grid;
          gap: 18px;
        }
        .fp-field {
          display: grid;
          gap: 8px;
        }
        .fp-field label {
          color: var(--fp-muted);
        }
        .fp-input-wrap {
          position: relative;
        }
        .fp-input {
          width: 100%;
          min-height: 58px;
          border: 1px solid var(--fp-line);
          border-radius: 18px;
          background: rgba(255, 253, 248, 0.82);
          color: var(--fp-ink);
          box-shadow: 0 1px 0 rgba(15, 45, 31, 0.05);
          box-sizing: border-box;
          outline: none;
          font-size: 17px;
          line-height: 1.35;
          padding: 0 48px 0 18px;
          transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
        }
        .fp-input::placeholder { color: #aaa39b; }
        .fp-input:focus {
          border-color: var(--fp-primary);
          background: var(--fp-panel);
          box-shadow: 0 0 0 4px rgba(15, 45, 31, 0.1);
        }
        .fp-input:disabled {
          color: #6c746f;
          background: #f0eee8;
          cursor: not-allowed;
        }
        .fp-field-icon {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #837d75;
          pointer-events: none;
        }
        .fp-icon-button {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          border-radius: 14px;
          border: 0;
          background: transparent;
          color: #837d75;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .fp-icon-button:hover,
        .fp-icon-button:focus-visible {
          color: var(--fp-primary);
          background: var(--fp-primary-soft);
        }
        .fp-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          padding-top: 8px;
        }
        .fp-primary-btn,
        .fp-secondary-btn,
        .fp-link-btn {
          min-height: 48px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          cursor: pointer;
          transition: transform 180ms ease, background 180ms ease, color 180ms ease, box-shadow 180ms ease;
        }
        .fp-primary-btn {
          flex: 1 1 260px;
          border: 0;
          background: var(--fp-primary);
          color: #faf8f3;
          padding: 0 22px;
          font-size: 15px;
          font-weight: 650;
          box-shadow: 0 16px 30px rgba(15, 45, 31, 0.22);
        }
        .fp-primary-btn:hover {
          background: #092116;
          transform: translateY(-1px);
        }
        .fp-primary-btn:active {
          transform: translateY(0);
        }
        .fp-primary-btn:disabled,
        .fp-secondary-btn:disabled {
          opacity: 0.58;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        .fp-secondary-btn {
          border: 1px solid var(--fp-line);
          background: transparent;
          color: var(--fp-primary);
          padding: 0 18px;
          font-size: 14px;
          font-weight: 620;
        }
        .fp-secondary-btn:hover {
          background: var(--fp-primary-soft);
          border-color: #c9d8cc;
        }
        .fp-footer {
          margin-top: 32px;
          padding-top: 22px;
          border-top: 1px dashed var(--fp-line);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .fp-link-btn {
          border: 0;
          background: transparent;
          color: var(--fp-muted);
          min-height: 44px;
          padding: 0;
          font-size: 14px;
        }
        .fp-link-btn:hover {
          color: var(--fp-primary);
        }
        .fp-change-btn {
          color: var(--fp-primary);
          font-weight: 650;
        }
        .fp-spinner {
          width: 16px;
          height: 16px;
          border-radius: 999px;
          border: 2px solid rgba(250, 248, 243, 0.42);
          border-top-color: #faf8f3;
          animation: fp-spin 700ms linear infinite;
        }
        @keyframes fp-spin { to { transform: rotate(360deg); } }
        .fp-enter { animation: fp-enter 520ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes fp-enter {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fp-link-arrow { transition: transform 180ms ease; }
        .fp-primary-btn:hover .fp-link-arrow { transform: translateX(4px); }
        button:focus-visible,
        input:focus-visible {
          outline: 3px solid rgba(212, 163, 86, 0.58);
          outline-offset: 3px;
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
          }
        }
        @media (max-width: 1023px) {
          .fp-shell {
            background:
              radial-gradient(circle at 90% 0%, rgba(231, 239, 233, 0.96), transparent 18rem),
              var(--fp-paper);
          }
          .fp-grid { display: block; }
          .fp-side { display: none; }
          .fp-form-side {
            min-height: 100dvh;
            align-items: center;
            padding: 24px;
          }
          .fp-mobile-brand {
            display: inline-flex;
            margin-bottom: 44px;
          }
          .fp-panel { width: min(100%, 520px); }
        }
        @media (max-width: 480px) {
          .fp-form-side {
            align-items: flex-start;
            padding: 28px 20px;
          }
          .fp-mobile-brand {
            margin-bottom: 72px;
          }
          .fp-title {
            font-size: 36px;
          }
          .fp-input {
            min-height: 56px;
            font-size: 16px;
            border-radius: 16px;
          }
          .fp-actions {
            display: grid;
          }
          .fp-primary-btn,
          .fp-secondary-btn {
            width: 100%;
          }
        }
      `}</style>

      <div className="fp-grid">
        <section className="fp-side" aria-label="Password reset security details">
          <button type="button" onClick={() => navigate("/")} className="fp-brand">
            <span className="fp-brand-mark">
              <KeyRound size={20} />
            </span>
            <span>
              <span className="fp-serif block text-[22px] font-semibold">Teamlio</span>
              <span className="fp-eyebrow" style={{ color: "#aec4b5" }}>Secure account access</span>
            </span>
          </button>

          <div className="fp-enter">
            <div className="fp-eyebrow mb-5" style={{ color: "var(--fp-gold)" }}>Password reset</div>
            <h1 className="fp-serif fp-side-title">Get back in with a code from your inbox.</h1>
            <p className="fp-side-copy">
              A short verification step protects the account before the new password is accepted.
            </p>
          </div>

          <div className="fp-proof-row">
            <div className="fp-proof">
              <MailCheck size={20} />
              <strong>Email code</strong>
              <span>Sent to the work email on the account.</span>
            </div>
            <div className="fp-proof">
              <Clock3 size={20} />
              <strong>5-minute window</strong>
              <span>Codes expire quickly to reduce risk.</span>
            </div>
            <div className="fp-proof">
              <ShieldCheck size={20} />
              <strong>Protected reset</strong>
              <span>Password changes only after verification.</span>
            </div>
          </div>
        </section>

        <section className="fp-form-side">
          <div className="fp-panel fp-enter">
            <button type="button" onClick={() => navigate("/")} className="fp-brand fp-mobile-brand">
              <span className="fp-brand-mark">
                <KeyRound size={20} />
              </span>
              <span>
                <span className="fp-serif block text-[22px] font-semibold" style={{ color: "var(--fp-primary)" }}>Teamlio</span>
                <span className="fp-eyebrow" style={{ color: "var(--fp-muted)" }}>Secure account access</span>
              </span>
            </button>

            <div className="fp-progress">
              <span className="fp-eyebrow" style={{ color: "var(--fp-muted)" }}>
                {step === "email" ? "Step 01 of 02" : "Step 02 of 02"}
              </span>
              <span className="fp-progress-track" aria-hidden>
                <span className="fp-progress-dot" data-active={step === "email"} />
                <span className="fp-progress-dot" data-active={step === "sent"} />
              </span>
            </div>

            <div className="fp-kicker">
              <LockKeyhole size={15} />
              <span className="fp-eyebrow">Account recovery</span>
            </div>

            <h2 className="fp-serif fp-title">
              Reset your <span style={{ fontStyle: "italic" }}>password.</span>
            </h2>
            <p className="fp-lede">
              {step === "email"
                ? "Enter your work email and we will send the same verification code used in Profile security."
                : `Enter the code sent to ${normalizedEmail} and choose a new password.`}
            </p>

            {status && (
              <div
                role={status.tone === "error" ? "alert" : "status"}
                aria-live="polite"
                className="fp-status"
                data-tone={status.tone}
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{status.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="fp-form">
              <div className="fp-field">
                <label htmlFor="forgot-email" className="fp-eyebrow">Work email</label>
                <div className="fp-input-wrap">
                  <input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={step === "sent" || busy}
                    placeholder="you@company.com"
                    autoComplete="email"
                    className="fp-input"
                  />
                  <Mail className="fp-field-icon h-4 w-4" />
                </div>
              </div>

              {step === "sent" && (
                <>
                  <div className="fp-field">
                    <label htmlFor="forgot-otp" className="fp-eyebrow">Verification code</label>
                    <div className="fp-input-wrap">
                      <input
                        id="forgot-otp"
                        value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="123456"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className="fp-input"
                      />
                      <MailCheck className="fp-field-icon h-4 w-4" />
                    </div>
                  </div>

                  <div className="fp-field">
                    <label htmlFor="forgot-new-password" className="fp-eyebrow">New password</label>
                    <div className="fp-input-wrap">
                      <input
                        id="forgot-new-password"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className="fp-input"
                      />
                      <button
                        type="button"
                        className="fp-icon-button"
                        onClick={() => setShowNewPassword((value) => !value)}
                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                      >
                        {showNewPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>

                  <div className="fp-field">
                    <label htmlFor="forgot-confirm-password" className="fp-eyebrow">Confirm new password</label>
                    <div className="fp-input-wrap">
                      <input
                        id="forgot-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className="fp-input"
                      />
                      <button
                        type="button"
                        className="fp-icon-button"
                        onClick={() => setShowConfirmPassword((value) => !value)}
                        aria-label={showConfirmPassword ? "Hide password confirmation" : "Show password confirmation"}
                      >
                        {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div className="fp-actions">
                <button type="submit" disabled={busy} className="fp-primary-btn">
                  {busy ? (
                    <>
                      <span className="fp-spinner" />
                      <span>{busyLabel}</span>
                    </>
                  ) : (
                    <>
                      <span>{buttonLabel}</span>
                      <ArrowRight className="w-4 h-4 fp-link-arrow" />
                    </>
                  )}
                </button>

                {step === "sent" && (
                  <button type="button" onClick={sendCode} disabled={busy} className="fp-secondary-btn">
                    <RotateCcw className="w-4 h-4" />
                    Resend code
                  </button>
                )}
              </div>
            </form>

            <div className="fp-footer">
              <button type="button" onClick={() => navigate("/login")} className="fp-link-btn">
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </button>

              {step === "sent" && (
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setOtp("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setStatus(null);
                  }}
                  className="fp-link-btn fp-change-btn"
                >
                  Change email
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
